import { FC, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Close, ExpandMore } from '@mui/icons-material';
import { IconButton } from '@mui/material';

import { getPortalContainer, useAppDispatch, useAppSelector } from '../../lib';
import { closeSidebar, setSidebarContent } from '../../store';
import { createPortal } from 'react-dom';

export const Sidebar: FC = () => {
  const { sidebarContent, sidebarVisible } = useAppSelector((state) => state.app);
  const dispatch = useAppDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const [scrollButtonVisible, setScrollButtonVisible] = useState(false);

  const getIsOverflowing = useCallback(() => {
    if (!ref.current) {
      return false;
    }

    return ref.current.scrollHeight - ref.current.clientHeight >= 20;
  }, []);

  const isOverflowing = getIsOverflowing();

  const handleCloseClick = useCallback(() => {
    dispatch(closeSidebar());
  }, [dispatch]);

  const onTransitionEnd = () => {
    if (!sidebarVisible) {
      dispatch(setSidebarContent({ content: null }));
    }
  };

  const handleScrollButtonClick = useCallback(() => {
    if (!ref.current) {
      return;
    }

    ref.current.scroll({ top: ref.current.scrollHeight, behavior: 'smooth' });
  }, []);

  const handleScroll = useCallback(() => {
    if (!ref.current) {
      return;
    }

    if (ref.current.scrollTop === 0) {
      setScrollButtonVisible(true);
      return;
    }

    if (scrollButtonVisible) {
      setScrollButtonVisible(false);
    }
  }, [scrollButtonVisible]);

  useEffect(() => {
    if (!sidebarVisible || !ref.current) {
      return;
    }

    if (getIsOverflowing()) {
      setScrollButtonVisible(true);
    }
  }, [sidebarVisible, getIsOverflowing]);

  return createPortal(
    <SidebarContainer
      visible={sidebarVisible}
      onTransitionEnd={onTransitionEnd}
      ref={ref}
      onScroll={isOverflowing ? handleScroll : undefined}
    >
      <SidebarHeader>
        <IconButton onClick={handleCloseClick} size="large">
          <Close fontSize="large" />
        </IconButton>
      </SidebarHeader>

      <SidebarContent>{sidebarContent}</SidebarContent>

      {scrollButtonVisible && (
        <ScrollButton onClick={handleScrollButtonClick}>
          <ExpandMore />
        </ScrollButton>
      )}
    </SidebarContainer>,
    getPortalContainer()
  );
};

const SidebarContainer = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100%;
  z-index: 1200;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease-in-out;
  transform: ${(p) => (p.visible ? 'translateX(0)' : 'translateX(100%)')};
  background-color: #fff;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.25);
  overflow-y: auto;
`;

const SidebarHeader = styled.div`
  display: flex;
  margin-bottom: 20px;
`;

const SidebarContent = styled.div`
  padding: 0 16px 24px;
`;

const ScrollButton = styled(IconButton)`
  position: absolute;
  bottom: 8px;
  right: 180px;
  background-color: #fff;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.25);

  :hover {
    background-color: #fff;
  }
`;
